/**
 * Auto-fit a column to the narrowest width that still shows its header and the widest rendered cell without clipping.
 *
 * The grid uses `table-layout: fixed` with `overflow: hidden; text-overflow: ellipsis`, so a cell's own `scrollWidth`
 * reports the clipped width, not the intrinsic content width — and flex cells that stretch to `width: 100%` report the
 * current column width regardless of content. To measure the true content width we clone each cell off-screen with its
 * layout constraints neutralized (auto width, no flex stretch, visible overflow, nowrap) and read the laid-out box.
 *
 * Only currently-rendered cells are measured. With virtualization that means the visible rows plus the header, which
 * matches the conventional "fit to visible content" behavior of spreadsheet auto-size.
 */

/** Small slack so antialiasing/subpixel rounding never re-triggers the ellipsis on the fitted width. */
const AUTO_FIT_BUFFER_PX = 4;

function neutralizeLayout(el: HTMLElement): void {
  el.style.width = "auto";
  el.style.minWidth = "0";
  el.style.maxWidth = "none";
  el.style.flex = "0 0 auto";
  el.style.overflow = "visible";
  el.style.whiteSpace = "nowrap";
  el.style.textOverflow = "clip";
}

/** Width of a cell's content laid out at its natural size, including the cell's own horizontal padding. */
function measureContentWidth(cell: HTMLElement): number {
  const clone = cell.cloneNode(true) as HTMLElement;
  neutralizeLayout(clone);
  for (const child of clone.querySelectorAll<HTMLElement>("*")) {
    neutralizeLayout(child);
  }
  clone.style.position = "absolute";
  clone.style.top = "-9999px";
  clone.style.left = "-9999px";
  clone.style.height = "auto";
  clone.style.visibility = "hidden";
  clone.style.pointerEvents = "none";
  clone.style.display = "inline-block";

  document.body.appendChild(clone);
  const width = clone.getBoundingClientRect().width;
  clone.remove();
  return width;
}

/**
 * Measure the widest natural content across a column's header and rendered body cells.
 *
 * @param tableEl - the `<table>` element whose cells carry `data-col-id`.
 * @param columnId - the column to measure.
 * @returns the content width in px, or `null` when nothing is rendered or measurement yields zero (e.g. jsdom).
 */
export function measureColumnContentWidth(tableEl: HTMLElement, columnId: string): number | null {
  const cells = tableEl.querySelectorAll<HTMLElement>(`[data-col-id="${columnId}"]`);
  let widest = 0;
  for (const cell of cells) {
    widest = Math.max(widest, measureContentWidth(cell));
  }
  return widest > 0 ? widest : null;
}

/**
 * Clamp a measured content width into the column's allowed range, adding a buffer first.
 *
 * The buffer is applied before clamping so a near-empty column still collapses to its `minSize` floor rather than the
 * floor plus slack.
 */
export function clampAutoFitWidth(
  measured: number,
  { minSize, maxSize, buffer = AUTO_FIT_BUFFER_PX }: { minSize?: number; maxSize?: number; buffer?: number } = {},
): number {
  const target = Math.ceil(measured) + buffer;
  const min = minSize ?? 0;
  const max = maxSize ?? Number.POSITIVE_INFINITY;
  return Math.min(Math.max(target, min), max);
}
